import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";

import enUS from "@/i18n/locales/en-US/translation.json";
import ptBR from "@/i18n/locales/pt-BR/translation.json";
import esES from "@/i18n/locales/es-ES/translation.json";

export const SUPPORTED_LOCALES = ["en-US", "pt-BR", "es-ES"] as const;

export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];

/** Variante regional que servimos para cada idioma. */
const LANGUAGE_TO_LOCALE: Record<string, SupportedLocale> = {
  en: "en-US",
  pt: "pt-BR",
  es: "es-ES",
};

/**
 * Mapeia idiomas/variantes (ex: pt-PT → pt-BR) ou retorna `null` se não for suportado.
 *
 * Retornar `null` é fundamental para o i18next ignorar a entrada atual no `supportedLngs` 
 * e continuar a busca na lista do `navigator.languages`.
 */
export function mapDetectedLanguage(detected: string | null | undefined): SupportedLocale | null {
  if (!detected) return null;
  const normalized = detected.replace("_", "-").toLowerCase();
  const exact = SUPPORTED_LOCALES.find((locale) => locale.toLowerCase() === normalized);
  if (exact) return exact;
  return LANGUAGE_TO_LOCALE[normalized.split("-")[0]] ?? null;
}

/** Mapeia o idioma da conta (servidor) para um locale válido, com fallback 'en-US'. 
* Para o navegador, use `mapDetectedLanguage`. 
*/
export function toSupportedLocale(detected: string | null | undefined): SupportedLocale {
  return mapDetectedLanguage(detected) ?? "en-US";
}

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      "en-US": { translation: enUS },
      "pt-BR": { translation: ptBR },
      "es-ES": { translation: esES },
    },
    // Sem `lng`. Ele era `"en-US"` fixo, o que anulava qualquer detecção: a tela
    // de login e a landing saíam em inglês para todo mundo, e o idioma só chegava
    // em `authStore.ts`, depois do `loadUser()` — isto é, depois do login.
    fallbackLng: "en-US",
    supportedLngs: [...SUPPORTED_LOCALES],
    detection: {
      // Só o navegador. `querystring` e `path` não existem neste app, e cache em
      // `localStorage` faria o `changeLanguage` do login gravar a preferência de
      // uma conta na máquina — a próxima pessoa a abrir `/login` ali veria o
      // idioma da anterior. A preferência da conta continua vindo do servidor.
      order: ["navigator"],
      caches: [],
      // Retorna o idioma original sem fallback fixo para permitir o retry no próximo idioma do navegador.
      convertDetectedLanguage: (lng: string) => mapDetectedLanguage(lng) ?? lng,
    },
    defaultNS: "translation",
    ns: ["translation"],
    // Required: frases com "." não viram chave aninhada
    keySeparator: false,
    nsSeparator: false,
    interpolation: {
      escapeValue: false,
    },
  });

export default i18n;
